<template>
  <Loading v-if="loading" />
  <div v-else>
    <div v-if="config" class="mt-4">
      <div class="grid grid-cols-3 max-w-lg gap-6">
        <!-- Email delay -->
        <div>
          <div><b>EOD Submission delay</b></div>
          <small>Specify the time-delay before sending a submitted EOD.</small>
        </div>
        <div class="col-span-2">
          <ul class="leftx mb-4">
            <li>
              <vs-radio v-model="config.timeDelayType" vs-name="delayType" vs-value="RelativeTime">Time delay</vs-radio>
            </li>
            <li>
              <vs-radio v-model="config.timeDelayType" vs-name="delayType" vs-value="AbsoluteTime">At specific time</vs-radio>
            </li>
          </ul>

          <div v-if="config.timeDelayType === 'RelativeTime'">
            <vs-slider v-model="relativeDelayMinutes" :max="1440" :step="15" text-fixed="Minutes" />
            <span class="flex gap-1 items-center font-bold" v-if="relativeDelayMinutes > 0">
              <span>Wait </span>
              <vs-chip color="primary">{{ parsedRelativeTimeDelay }}</vs-chip>
              <span> before sending</span>
            </span>
            <vs-chip color="primary" v-else>Send instantly</vs-chip>
          </div>
          <div v-else-if="config.timeDelayType === 'AbsoluteTime'">
            <vs-input v-model="absoluteTime" type="time" class="mb-2" />
            <span class="flex gap-1 items-center font-bold">
              <span>Send the following day at </span>
              <vs-chip color="primary">{{ absoluteTime }}</vs-chip>
              <span> (UTC time zone)</span>
            </span>
            <small>NB: Will send instantly if submitted after this time</small>
          </div>
        </div>

        <!-- Email recipients -->
        <div>
          <div><b>EOD recipients</b></div>
          <small>Roles that receive a copy of the EOD by email</small>
        </div>
        <div class="col-span-2">
          <vs-select multiple autocomplete v-model="config.recipientRoles" class="w-full">
            <vs-select-item :key="idx" :value="role.id" :text="role.title + ' (' + role.id + ')'"
              v-for="(role, idx) in roles" />
          </vs-select>
        </div>

        <!-- Dynamic rating question rules -->
        <template v-for="qRule in config.questionRules">
          <div :key="'label-' + qRule.questionId">
            <div><b>{{ qRule.questionTitle }}</b> recipients</div>
            <small>Notification rules based on answer</small>
          </div>
          <div class="col-span-2" :key="'rules-' + qRule.questionId">
            <vs-select
              v-for="(rr, rrIdx) in qRule.ratingRules"
              :key="rrIdx"
              :label="rr.label || 'Rating ' + rr.rating"
              multiple
              autocomplete
              v-model="rr.roles"
              class="w-full"
            >
              <vs-select-item :key="idx" :value="role.id" :text="role.title + ' (' + role.id + ')'"
                v-for="(role, idx) in roles" />
            </vs-select>
          </div>
        </template>
      </div>

      <div class="mt-4" v-if="publishedTemplate && notifiableQuestions.length > 0">
        <vs-button size="small" @click="syncQuestionRules" class="mb-4">
          Sync rules with published template
        </vs-button>
        <small class="block mb-4">
          This will add rules for any new rating / yes-no questions and remove rules for deleted questions.
        </small>
      </div>

      <div class="mt-4">
        <vs-button @click="onClickUpdate">Update</vs-button>
      </div>
    </div>

    <div v-else class="mt-4">
      <p>No notification config found for this country. Save to create one.</p>
      <vs-button @click="createConfig" class="mt-2">Create Config</vs-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import parseDelay from "parse-duration";
import humanizeDuration from "humanize-duration";

export default {
  props: {
    activeCountryId: { type: String, default: null },
  },
  data() {
    return {
      loading: true,
      config: null,
      publishedTemplate: null,
      relativeDelayMinutes: 0,
      absoluteTime: "08:00",
    };
  },
  computed: {
    ...mapGetters({
      roles: "roles",
      storeActiveCountry: "venues/activeCountry",
    }),
    countryId() {
      return this.activeCountryId || this.storeActiveCountry.id;
    },
    parsedRelativeTimeDelay() {
      return humanizeDuration(parseDelay(this.relativeDelayMinutes + "m"));
    },
    notifiableQuestions() {
      if (!this.publishedTemplate) return [];
      return (this.publishedTemplate.questions || []).filter(function (q) { return q.type === "rating" || q.type === "yesNo"; });
    },
  },
  watch: {
    countryId() {
      this.loadConfig();
    },
  },
  methods: {
    syncQuestionRules() {
      if (!this.config || !this.publishedTemplate) return;

      var existingRules = this.config.questionRules || [];
      var questions = this.notifiableQuestions;

      var updatedRules = questions.map(function (q) {
        var existing = existingRules.find(function (r) { return r.questionId === q.id; });
        var existingRatingRules = existing ? (existing.ratingRules || []) : [];

        var ratingRules = [];
        if (q.type === "yesNo") {
          var noRule = existingRatingRules.find(function (rr) { return rr.rating === 0; });
          var yesRule = existingRatingRules.find(function (rr) { return rr.rating === 1; });
          ratingRules.push({ rating: 0, label: "No", roles: noRule ? noRule.roles : [] });
          ratingRules.push({ rating: 1, label: "Yes", roles: yesRule ? yesRule.roles : [] });
        } else {
          for (var i = q.minValue; i <= q.maxValue; i++) {
            var label = q.labels && q.labels[i] ? q.labels[i] : "" + i;
            var existingRR = existingRatingRules.find(function (rr) { return rr.rating === i; });
            ratingRules.push({ rating: i, label: label, roles: existingRR ? existingRR.roles : [] });
          }
        }
        return { questionId: q.id, questionTitle: q.title, ratingRules: ratingRules };
      });

      this.config.questionRules = updatedRules;
      this.$vs.notify({ title: "Synced", text: "Question rules synced with published template", color: "success" });
    },

    createConfig() {
      this.config = {
        country: this.countryId,
        timeDelayType: "RelativeTime",
        relativeTimeDelay: "00:00:00",
        absoluteTimeDelay: "08:00:00",
        recipientRoles: [],
        questionRules: [],
      };
    },

    async onClickUpdate() {
      try {
        var relativeTimeHours = Math.floor(this.relativeDelayMinutes / 60);
        var relativeTimeMinutes = this.relativeDelayMinutes % 60;
        if (relativeTimeHours.toString().length === 1) relativeTimeHours = "0" + relativeTimeHours;
        if (relativeTimeMinutes.toString().length === 1) relativeTimeMinutes = "0" + relativeTimeMinutes;

        var modified = Object.assign({}, this.config, {
          relativeTimeDelay: relativeTimeHours + ":" + relativeTimeMinutes + ":00",
          absoluteTimeDelay: this.absoluteTime + ":00",
        });

        this.$store.commit("eodNotificationConfig/SET_CONFIG", modified);
        await this.$store.dispatch("eodNotificationConfig/updateConfig");

        this.$vs.notify({ title: "Success", text: "EOD notification config updated", color: "success" });
      } catch (err) {
        this.$vs.notify({ title: "Error", text: "Could not update config", color: "danger" });
      }
    },

    async loadConfig() {
      this.loading = true;

      await this.$store.dispatch("getRoles");

      try {
        var rawConfig = await this.$store.dispatch("eodNotificationConfig/getConfig", this.countryId);
        if (rawConfig) {
          var relParts = (rawConfig.relativeTimeDelay || "00:00:00").split(":");
          this.relativeDelayMinutes = parseInt(relParts[0]) * 60 + parseInt(relParts[1]);
          this.absoluteTime = (rawConfig.absoluteTimeDelay || "08:00:00").split(":").slice(0, 2).join(":");
          this.config = rawConfig;
        } else {
          this.config = null;
        }
      } catch (_) {
        this.config = null;
      }

      try {
        var apiGetLatest = require("@/api/life/eodTemplates/getLatestEodTemplate");
        this.publishedTemplate = await apiGetLatest.get(this.countryId);
      } catch (_) {
        this.publishedTemplate = null;
      }

      this.loading = false;
    },
  },
  async mounted() {
    await this.loadConfig();
  },
};
</script>
